/* eslint-disable consistent-return */
import { ApolloClient, ApolloLink, InMemoryCache, NormalizedCacheObject, Reference } from '@apollo/client'
import { isEmpty, isNull } from 'lodash'

import { AnnouncementsQueryCache } from './entities/announcements/announcementsQueryCache'
import { PaginatedCompaniesQueryCache } from './entities/companies/paginatedCompaniesQueryCache'
import { PaginatedCrmIntegrationObjectsQueryCache } from './entities/crmIntegrations/paginatedCrmIntegrationObjectsQueryCache'
import { PaginatedDealsOwnersQueryCache } from './entities/dealOwners/paginatedDealsOwnersQueryCache'
import { PaginatedDealsQueryCache } from './entities/deals/paginatedDealsQueryCache'
import { PaginatedIntegrationTasksQueryCache } from './entities/integrationTasks/paginatedIntegrationTasksQueryCache'
import { KpisQueryCache } from './entities/measurements/kpisQueryCache'
import { ObjectivesQueryCache } from './entities/measurements/objectivesQueryCache'
import { MessageGroupsCache } from './entities/messageGroups/messageGroupsQueryCache'
import { PaginatedCompetingCompaniesNewsArticlesQueryCache } from './entities/news/paginatedCompetingCompaniesNewsArticlesQueryCache'
import { getCacheCookie, mergePaginatedObj } from './helpers'
import { displayMessageGroupUserObjTypename } from './typenames'

import { tasksFieldPolicyOptions } from '~apollo/entities/tasks/paginatedTasks'
import { PaginatedGroupsFieldPolicy } from '~apollo/paginatedGroupsFieldPolicy'
import { createPaginatedAssetsQueryCache, createPaginatedAssetGroupsQueryCache } from '~entities/assets'
import {
  createPaginatedGongInsightIntegrationItemsQueryCache,
  createPaginatedIntercomInsightIntegrationItemsQueryCache,
  createPaginatedZendeskInsightIntegrationItemsQueryCache,
  createPaginatedClariCopilotInsightIntegrationItemsQueryCache,
  createPaginatedZoomInsightIntegrationItemsQueryCache,
} from '~entities/integrations'
import { createPaginatedReleaseNotesQueryCache } from '~entities/release-notes'
import { createPaginatedTestimonialsQueryCache } from '~entities/testimonials'
import { CookieDocument } from '~generated/hooks'
import introspectionResult from '~generated/introspection-result'
import { CookieQuery, DisplayMessageGroupUserFragment, InsightPartTextFragment } from '~generated/operations'
import {
  FilterLaunchInput,
  FindIntegrationChannelsInput,
  FilterTasksInput,
  OnboardingGoal,
  Theme,
  InsightSource,
} from '~generated/types'
import { AUTH_DATA_KEYS } from '~lib/constants'
import { calculateAvatarColor, isBrowser, percentage } from '~lib/helpers'
import { ProductBoardItemsFilterKeys } from '~modules/productRoadmapItems/usecases/useFilterProductBoardItems'
import { ProductBoardItemsSortKeys } from '~modules/productRoadmapItems/usecases/useSortProductBoardItems'

export default function createApolloClient(initialState = {}, link: ApolloLink): ApolloClient<NormalizedCacheObject> {
  return new ApolloClient({
    ssrMode: !isBrowser(),
    link,
    connectToDevTools: process.env.NODE_ENV !== 'production',
    cache: new InMemoryCache({
      possibleTypes: introspectionResult.possibleTypes,
      typePolicies: {
        Query: {
          fields: {
            integrationProject(data, { args, toReference }) {
              if (!args.id) return null

              return toReference({
                __typename: 'IntegrationProjectObj',
                id: args.id,
              })
            },

            launch(data, { args, toReference }) {
              if (!args.id) return null

              return toReference({
                __typename: 'LaunchObj',
                id: args.id,
              })
            },

            launchModule(_, { args, toReference }) {
              if (!args.id) return null

              return toReference({
                __typename: 'LaunchModuleObj',
                id: args.id,
              })
            },

            user(data, { args, toReference }) {
              if (!args.id) return null

              return toReference({
                __typename: 'UserObj',
                id: args.id,
              })
            },

            findIntegrationChannels: {
              keyArgs: (() => {
                const filterKeys: KeysEnum<FindIntegrationChannelsInput> = {
                  sendAs: true,
                  source: true,
                }

                return ['filter', Object.keys(filterKeys)]
              })(),
              merge(existing, incoming, { readField }) {
                const docs = existing ? { ...existing.docs } : {}
                incoming.docs.forEach((doc) => {
                  docs[readField<string>('id', doc)] = doc
                })
                return {
                  nextCursor: incoming.nextCursor,
                  docs,
                }
              },

              read(existing) {
                if (existing) {
                  return {
                    nextCursor: existing.nextCursor,
                    docs: Object.values(existing.docs),
                  }
                }
              },
            },

            templates: {
              keyArgs: false,
              merge: mergePaginatedObj({ field: 'id' }),
            },

            paginatedLaunches: {
              // key args with filter in some cases does not work correct and query return undefined except of correct object
              // TODO - research apollo for this error
              keyArgs: (() => {
                const filterKeys: KeysEnum<FilterLaunchInput> = {
                  launchId: true,
                  launchTier: true,
                  launchTypeId: true,
                  ownerId: true,
                  teamMembers: true,
                  search: true,
                  status: true,
                  launchTags: true,
                  launchMonths: true,
                  startDate: true,
                  endDate: true,
                  createdAtGte: true,
                }

                return ['filter', Object.keys(filterKeys)]
              })(),
              merge: (existing = {}, incoming = {}, { variables }) => {
                return {
                  ...existing,
                  ...incoming,
                  docs:
                    variables.pagination?.page === 1
                      ? incoming?.docs || []
                      : [...(existing.docs || []), ...(incoming.docs || [])],
                }
              },
              read: (launches, { variables, readField }) => {
                const { filter } = variables || {}
                const { status } = filter || {}

                const existingDocs = [...(launches?.docs || [])]

                existingDocs?.sort((a, b) => {
                  const aDate = new Date(readField('launchDate', a) as Date)
                  const bDate = new Date(readField('launchDate', b) as Date)

                  return (status === 'PAST' ? aDate > bDate : bDate > aDate) ? -1 : 1
                })

                return {
                  ...launches,
                  docs: existingDocs,
                }
              },
            },

            publicPaginatedLaunches: {
              // key args with filter in some cases does not work correct and query return undefined except of correct object
              // TODO - research apollo for this error
              keyArgs: (() => {
                const filterKeys: KeysEnum<FilterLaunchInput> = {
                  launchId: true,
                  launchTier: true,
                  launchTypeId: true,
                  ownerId: true,
                  teamMembers: true,
                  search: true,
                  status: true,
                  launchTags: true,
                  launchMonths: true,
                  startDate: true,
                  endDate: true,
                  createdAtGte: true,
                }

                return ['filter', Object.keys(filterKeys)]
              })(),
              merge: (existing = {}, incoming = {}, { variables }) => {
                return {
                  ...existing,
                  ...incoming,
                  docs:
                    variables.pagination?.page === 1
                      ? incoming?.docs || []
                      : [...(existing.docs || []), ...(incoming.docs || [])],
                }
              },
              read: (launches, { variables, readField }) => {
                const { filter } = variables || {}
                const { status } = filter || {}

                const existingDocs = [...(launches?.docs || [])]

                existingDocs?.sort((a, b) => {
                  const aDate = new Date(readField('launchDate', a) as Date)
                  const bDate = new Date(readField('launchDate', b) as Date)

                  return (status === 'PAST' ? aDate > bDate : bDate > aDate) ? -1 : 1
                })

                return {
                  ...launches,
                  docs: existingDocs,
                }
              },
            },

            customOrganizationTemplates: {
              merge: (_, incoming) => {
                return incoming
              },
            },

            paginatedTasks: (() => {
              const filterKeys: KeysEnum<FilterTasksInput> = {
                tagIds: true,
                assigneeIds: true,
                launchIds: true,
                ownerIds: true,
                types: true,
                statuses: true,
                priorities: true,
                dueDateLte: true,
                dueDateGte: true,
                query: true,
              }

              return new PaginatedGroupsFieldPolicy(['filter', Object.keys(filterKeys)], tasksFieldPolicyOptions)
            })(),

            paginatedIntegrationTasks: new PaginatedIntegrationTasksQueryCache(),

            surveys: {
              merge: (existing = {}, incoming, { readField }) => {
                const existingDocs = [...(existing.docs || [])]
                const existingDocsIds = new Set(existingDocs.map((ref) => readField('id', ref)))
                const incomingDocs = incoming?.docs?.filter((ref) => !existingDocsIds.has(readField('id', ref)))
                const docs = [...existingDocs, ...(incomingDocs || [])]

                return {
                  ...existing,
                  ...incoming,
                  docs,
                }
              },
              read: (surveys) => surveys,
            },

            paginatedReleaseNotes: createPaginatedReleaseNotesQueryCache(),

            paginatedClariCopilotInsightIntegrationItems:
              createPaginatedClariCopilotInsightIntegrationItemsQueryCache(),
            paginatedGongInsightIntegrationItems: createPaginatedGongInsightIntegrationItemsQueryCache(),
            paginatedIntercomInsightIntegrationItems: createPaginatedIntercomInsightIntegrationItemsQueryCache(),
            paginatedZendeskInsightIntegrationItems: createPaginatedZendeskInsightIntegrationItemsQueryCache(),
            paginatedZoomInsightIntegrationItems: createPaginatedZoomInsightIntegrationItemsQueryCache(),

            paginatedFeedbackPosts: {
              keyArgs: ['filter', 'accountsFilter', 'sort', 'pagination', ['limit']],
            },

            paginatedInsights: {
              keyArgs: ['filter', 'sort'],
            },

            paginatedInsightsTrendItems: {
              keyArgs: ['filter', 'sort'],
            },

            paginatedPersonas: {
              merge: mergePaginatedObj({ field: 'id' }),
              read: (existing) => existing,
            },

            paginatedCompanies: new PaginatedCompaniesQueryCache(),
            paginatedCompanyGroups: {
              keyArgs: ['filter', 'sort'],
              merge: mergePaginatedObj({ field: 'id' }),
            },
            paginatedCompaniesByGroup: {
              keyArgs: ['filter', 'sort'],
              merge: mergePaginatedObj({ field: 'id' }),
            },
            paginatedCDPAccounts: {
              keyArgs: ['filter', 'sort'],
              merge: mergePaginatedObj({ field: 'id' }),
            },

            paginatedDealNotes: {
              keyArgs: ['id', 'search'],
              merge: mergePaginatedObj({ field: 'lostReason' }),
              read: (existing) => existing,
            },

            paginatedTestimonials: createPaginatedTestimonialsQueryCache(),

            paginatedObjectives: new ObjectivesQueryCache(),

            objectives: new ObjectivesQueryCache(),
            kpis: new KpisQueryCache(),

            comments: {
              keyArgs: ['filter', ['parentId']],
            },

            tags: {
              keyArgs: ['input', ['ids', 'types', 'parentName', 'launchId']],
            },

            messageGroups: new MessageGroupsCache(),
            paginatedAnnouncements: new AnnouncementsQueryCache(),

            usersInOrganization: {
              keyArgs: ['search', 'pagination', ['limit']],
            },

            cookie: {
              read: (cookie) => getCacheCookie(cookie),
            },

            paginatedAssets: createPaginatedAssetsQueryCache(),
            publicLaunchPagePaginatedAssets: createPaginatedAssetsQueryCache(),
            paginatedAssetGroups: createPaginatedAssetGroupsQueryCache(),

            organizationIntegrations: {
              keyArgs: ['filter', ['productName']],
            },
            integrations: {
              keyArgs: ['integrationType'],
              // TODO - reserach in what case null appears inside integrations
              read: (data) => {
                return data?.filter(Boolean)
              },
            },
            getKeywords: {
              keyArgs: ['input', ['type', 'websiteName', 'sort']],
              read: (existing) => existing,
            },
            paginatedG2UserReviews: {
              keyArgs: ['companyName'],
              merge: mergePaginatedObj({ field: 'userName' }),
              read: (existing) => existing,
            },
            getPaginatedNews: {
              keyArgs: ['pageUrl'],
              merge: mergePaginatedObj({ field: 'title' }),
            },

            paginatedCompetingCompaniesNewsArticles: new PaginatedCompetingCompaniesNewsArticlesQueryCache(),

            paginatedProductBoardItems: {
              keyArgs: (() => {
                return [
                  'filter',
                  [...Object.keys(ProductBoardItemsFilterKeys)],
                  'sort',
                  [
                    ...Object.keys(ProductBoardItemsSortKeys).flatMap((sortKey) => [
                      sortKey,
                      ['order', 'customFieldId'],
                    ]),
                  ],
                ]
              })(),
              merge: mergePaginatedObj({ field: 'id' }),
              read: (existing) => existing,
            },

            productBoardItemsGroups: {
              keyArgs: (() => {
                return ['filter', [...Object.keys(ProductBoardItemsFilterKeys)], 'groupBy']
              })(),
              merge: mergePaginatedObj({ field: 'id' }),
              read: (existing) => existing,
            },

            paginatedCrmObjects: {
              keyArgs: ['input', ['crm'], 'filter', ['search']],
              merge: mergePaginatedObj({ field: 'id' }),
              read: (existing) => existing,
            },

            paginatedCrmIntegrationObjects: new PaginatedCrmIntegrationObjectsQueryCache(),

            paginatedDeals: new PaginatedDealsQueryCache(),

            paginatedDealsOwners: new PaginatedDealsOwnersQueryCache(),
          },
        },

        ChecklistObj: {
          keyFields: ['organizationId'],
        },

        PaginatedLaunchObj: {
          keyFields: false,
        },

        ProposedChannelObj: {
          keyFields: ['name'],
        },

        ProposedChannelsObj: {
          keyFields: ['launchId'],
        },

        GtmTimelineObj: {
          keyFields: ['launchId'],
        },

        PaginatedTaskObj: {
          keyFields: false,
        },

        CopilotLaunchTypePreferenceObj: {
          keyFields: ['launchType', ['id']],
        },

        LaunchTypeObj: {
          fields: {
            description: {
              read: (description, { readField }) => {
                // TODO
                const HINTS = {
                  Product: 'Launching a new product or major functionality',
                  'Feature/Improvement': 'Launching a smaller feature or improving an existing one',
                  Campaign: 'Launching a promotional initiative like a brand campaign, or a webinar',
                  Other: 'Launching other items like pricing or policy updates',
                }

                const name = readField<string>('name')

                return HINTS[name] || ''
              },
            },
          },
        },

        OrganizationObj: {
          fields: {
            users: {
              read: (users) => {
                // TO DO - remove filer when bug will be fixed on backed side
                // in some cases users has details as null
                return users?.filter(({ userDetails }) => userDetails)
              },
            },

            currentUser: {
              read: (_, { readField, cache }) => {
                const data = cache.readQuery<CookieQuery>({ query: CookieDocument })
                const userId = data?.cookie[AUTH_DATA_KEYS.USER_ID]

                const users: readonly any[] = readField('users') || []
                return users.find((user) => user.userId === userId)
              },
            },
          },
        },

        LaunchObj: {
          keyFields: ['id'],
          fields: {
            users: {
              read: (users) => {
                // TO DO - remove filer when bug will be fixed on backed side
                // in some cases users has details as null
                return users?.filter(({ userDetails }) => userDetails)
              },
            },

            modulesLaunchSections: {
              // TODO - fix backend to remove Copy & Assets and Task Management from the launch sections
              read: (_, { readField }) => {
                const launchSections: readonly Reference[] = readField('launchSections')
                const modulesLaunchSections: readonly Reference[] = launchSections?.filter((ref) => {
                  return readField('name', ref) !== 'Copy & Assets' && readField('name', ref) !== 'Task Management'
                })

                return modulesLaunchSections || []
              },
            },

            launchModules: {
              read: (_, { readField }) => {
                const launchSections: readonly Reference[] = readField('modulesLaunchSections')
                const launchModules: readonly Reference[] = launchSections?.flatMap((ref) => {
                  return readField('launchModules', ref)
                })

                return launchModules
              },
            },
          },
        },

        ProductBoardRoadmapObj: {
          fields: {
            items: {
              keyArgs: (() => {
                return [
                  'filter',
                  [...Object.keys(ProductBoardItemsFilterKeys)],
                  'sort',
                  [
                    ...Object.keys(ProductBoardItemsSortKeys).flatMap((sortKey) => [
                      sortKey,
                      ['order', 'customFieldId'],
                    ]),
                  ],
                ]
              })(),
              merge: mergePaginatedObj({ field: 'id' }),
              read: (existing) => existing,
            },
          },
        },

        ProductBoardReleaseObj: {
          fields: {
            items: {
              keyArgs: (() => {
                return [
                  'filter',
                  [...Object.keys(ProductBoardItemsFilterKeys)],
                  'sort',
                  [
                    ...Object.keys(ProductBoardItemsSortKeys).flatMap((sortKey) => [
                      sortKey,
                      ['order', 'customFieldId'],
                    ]),
                  ],
                ]
              })(),
              merge: mergePaginatedObj({ field: 'id' }),
              read: (existing) => existing,
            },
          },
        },

        TasksProgressObj: {
          fields: {
            notStartedPercentage: {
              read: (_, { readField }) => {
                const total: number = readField('totalCount') || 1
                const value: number = readField('notStartedCount') || 0
                return percentage(value, total) || 0
              },
            },
            inProgressPercentage: {
              read: (_, { readField }) => {
                const total: number = readField('totalCount') || 1
                const value: number = readField('inProgressCount') || 0
                return percentage(value, total) || 0
              },
            },
            inReviewPercentage: {
              read: (_, { readField }) => {
                const total: number = readField('totalCount') || 1
                const value: number = readField('inReviewCount') || 0
                return percentage(value, total) || 0
              },
            },
            completedPercentage: {
              read: (_, { readField }) => {
                const total: number = readField('totalCount') || 1
                const value: number = readField('completedCount') || 0
                return percentage(value, total) || 0
              },
            },
            canceledPercentage: {
              read: (_, { readField }) => {
                const total: number = readField('totalCount') || 1
                const value: number = readField('canceledCount') || 0
                return percentage(value, total) || 0
              },
            },
            delayedPercentage: {
              read: (_, { readField }) => {
                const total: number = readField('totalCount') || 1
                const value: number = readField('delayedCount') || 0
                return percentage(value, total) || 0
              },
            },
          },
        },

        TaskObj: {
          fields: {
            subtasks: (subtasks = []) => subtasks,
          },
        },

        PaginatedAssetGroupObj: {
          keyFields: ['key'],
        },

        ProductBoardItemObj: {
          fields: {
            personasIds: {
              read: (_, { readField }) => {
                const personas: readonly Reference[] = readField('personas')
                return personas?.map((ref) => readField('id', ref)) || []
              },
            },

            competitorsIds: {
              read: (_, { readField }) => {
                const competitors: readonly Reference[] = readField('competitors')
                return competitors?.map((ref) => readField('id', ref)) || []
              },
            },

            launchesIds: {
              read: (_, { readField }) => {
                const launches: readonly Reference[] = readField('launches')
                return launches?.map((ref) => readField('id', ref)) || []
              },
            },

            subItemsIds: {
              read: (_, { readField }) => {
                const subItems: readonly Reference[] = readField('subItems')
                return subItems?.map((ref) => readField('id', ref)) || []
              },
            },

            feedbackPostIds: {
              read: (_, { readField }) => {
                const feedbackPosts: readonly Reference[] = readField('feedbackPosts')
                return feedbackPosts?.map((ref) => readField('id', ref)) || []
              },
            },

            insightsIds: {
              read: (_, { readField }) => {
                const insights: readonly Reference[] = readField('insights')
                return insights?.map((ref) => readField('id', ref)) || []
              },
            },

            dealIds: {
              read: (_, { readField }) => {
                const deals: readonly Reference[] = readField('deals')
                return deals?.map((ref) => readField('id', ref)) || []
              },
            },

            revenueImpact: {
              read: (revenueImpact, { readField }) => {
                const deals: readonly Reference[] = readField('deals')
                const feedbackPosts: readonly Reference[] = readField('feedbackPosts')

                if (!deals || !feedbackPosts) return revenueImpact

                const dealsAmount = deals
                  .map((ref) => readField('amount', ref))
                  .reduce(
                    (total: number, amount: number) => total + (!Number.isNaN(Number(amount)) ? Number(amount) : 0),
                    0
                  )

                return feedbackPosts
                  .map((postRef) => readField('revenueImpact', postRef))
                  .reduce(
                    (total: number, revenueImpact: number) =>
                      total + (!Number.isNaN(Number(revenueImpact)) ? Number(revenueImpact) : 0),
                    dealsAmount
                  )
              },
            },
          },
        },

        FeedbackPostDto: {
          fields: {
            revenueImpact: (revenueImpact, { readField }) => {
              const deals: readonly Reference[] = readField('deals')

              if (!deals) return revenueImpact

              return deals
                .map((ref) => readField('amount', ref))
                .reduce(
                  (total: number, amount: number) => total + (!Number.isNaN(Number(amount)) ? Number(amount) : 0),
                  0
                )
            },
          },
        },

        ProductBoardKanbanCellObj: {
          keyFields: false,
        },
        ProductBoardItemsGroupObj: {
          keyFields: false,
        },

        PersonaObj: {
          fields: {
            name: (name) => {
              return name || 'Persona name'
            },
          },
        },
        // TODO create members array and move to it all fields related to memberships
        UserInOrganizationObj: {
          fields: {
            id: (_, { readField }) => {
              const userId = readField<string>('userId')
              if (userId) return userId

              const userRef = readField<Reference>('userDetails')
              return readField<string>('id', userRef)
            },
            email: (_, { readField }) => {
              const userRef = readField<Reference>('userDetails')
              return readField('email', userRef)
            },
            name: (_, { readField }) => {
              const userRef = readField<Reference>('userDetails')
              return readField('name', userRef)
            },
            jobTitle: (_, { readField }) => {
              const userRef = readField<Reference>('userDetails')
              return readField('jobTitle', userRef)
            },
            profileImage: (_, { readField }) => {
              const userRef = readField<Reference>('userDetails')
              return readField('profileImage', userRef)
            },
            avatarColor: (_, { readField }) => {
              const name = readField<string>('name')
              const email = readField<string>('email')
              return calculateAvatarColor(name || email)
            },
            // move to account or user settings
            primaryOnboardingGoal: (_, { readField }) => {
              const userRef = readField<Reference>('userDetails')
              return readField('primaryOnboardingGoal', userRef)
            },
            isEmailVerified: (_, { readField }) => {
              const userRef = readField<Reference>('userDetails')
              return readField('isEmailVerified', userRef)
            },
            theme: (_, { readField }) => {
              const userRef = readField<Reference>('userDetails')
              return readField('theme', userRef)
            },
          },
        },

        PublicUserObj: {
          fields: {
            email: (_) => {
              return ''
            },
            primaryOnboardingGoal: (_) => {
              return OnboardingGoal.LAUNCHES
            },
            theme: (_) => {
              return Theme.LIGHT
            },
            isEmailVerified: (_) => {
              return null
            },
            avatarColor: (_, { readField }) => {
              const name = readField<string>('name')
              const email = readField<string>('email')
              return calculateAvatarColor(name || email)
            },
          },
        },

        UserObj: {
          fields: {
            avatarColor: (_, { readField }) => {
              const name = readField<string>('name')
              const email = readField<string>('email')
              return calculateAvatarColor(name || email)
            },
          },
        },

        NonUserObj: {
          fields: {
            avatarColor: (_, { readField }) => {
              const name = readField<string>('name')
              const email = readField<string>('email')
              return calculateAvatarColor(name || email)
            },
          },
        },

        MessageGroupRecipientsObj: {
          fields: {
            displayUsers: {
              read: (_, { readField }) => {
                const usersData = readField<Reference[]>('usersData')
                const nonUsersData = readField<Reference[]>('nonUsersData')

                const displayUsers: DisplayMessageGroupUserFragment[] = (usersData || []).map((ref) => ({
                  __typename: displayMessageGroupUserObjTypename,
                  id: readField<string>('id', ref),
                  label: readField<string>('name', ref) || readField<string>('email', ref),
                  avatarColor: readField<string>('avatarColor', ref),
                }))

                const displayNonUsers: DisplayMessageGroupUserFragment[] = (nonUsersData || []).map((ref) => ({
                  __typename: displayMessageGroupUserObjTypename,
                  id: readField<string>('id', ref),
                  label: readField<string>('email', ref),
                  avatarColor: readField<string>('avatarColor', ref),
                }))

                return [...displayUsers, ...displayNonUsers]
              },
            },
          },
        },

        UserWithModulesObj: {
          fields: {
            saved: (saved = true) => saved,
          },
        },

        UserModuleRoleObj: {
          fields: {
            saved: (saved = true) => saved,
          },
        },

        CommentObj: {
          fields: {
            saved: (saved = true) => saved,
          },
        },

        PersonaDemographicsCategoryDto: {
          fields: {
            name: (name) => name || '',
            value: (value) => value || '',
            placeholder: (placeholder) => placeholder || 'Value',
          },
        },

        PersonaNoteDto: {
          fields: {
            title: (title) => title || '',
            text: (text) => text || '',
            placeholder: (placeholder) => placeholder || 'Text',
          },
        },

        InsightObj: {
          fields: {
            title: (_, { readField }) => {
              const name = readField('name')
              const metadata = readField('metadata')
              const summaryName = readField('summaryName', metadata as Reference)

              const user = readField('user')
              const company = readField('company')
              return summaryName || name || [user, company].filter(Boolean).join(' / ')
            },

            importedContent: (_, { readField }) => {
              const parts: readonly InsightPartTextFragment[] = readField('parts')

              if (isEmpty(parts)) return ''

              return parts.reduce((acc, { authorName, text }) => {
                if (authorName) {
                  if (text.startsWith('<p>') && text.endsWith('</p>')) {
                    return `${acc} <p><b>${authorName}</b>: ${text.slice(3, -4)}</p>`
                  }
                  return `${acc}<p><b>${authorName}</b>: ${text}</p>`
                }
                return `${acc}${text}`
              }, '')
            },

            isEditable: (_, { readField }) => {
              const source: InsightSource = readField('source')
              const content = readField('content')

              if (!source || source === InsightSource.CSV) {
                return true
              }

              if (source === InsightSource.SLACK) {
                return !isNull(content)
              }

              return false
            },

            isImportedFromIntegration: (_, { readField }) => {
              const source: InsightSource = readField('source')
              return source && source !== InsightSource.CSV
            },
          },
        },

        DealObj: {
          keyFields: ['id'],
          fields: {
            color(_, { readField }) {
              const name: string = readField('name')
              return calculateAvatarColor(name)
            },
          },
        },

        PhasesObj: {
          keyFields: ['id'],
        },

        CompanyObj: {
          keyFields: ['id'],
        },
      },
    }).restore(initialState),
  })
}
